// APAlignment.jsx
// Course-grade vs AP-score alignment explorer.
//
// Data model: the 2025 dataset is hardcoded below in `ALIGNMENT_DATA_2025`.
// When additional years are available in Firestore (collection: `apAlignment`),
// swap the local `useAlignmentData` hook for a Firestore-backed one — the
// shape is already compatible. See the comment on `useAlignmentData` below.
import { useState, useRef } from "react";

// ─── Data ────────────────────────────────────────────────────────────────────
// One row per teacher-section, sourced from AP_Score_comparison - TEMP.csv (2025).
// Section-level AVG aggregate rows from the CSV are excluded so each data point
// represents a single section on the scatter.
const ALIGNMENT_DATA_2025 = [
  { teacher: "T1", subject: "AP Biology",                   grade: 88.00, gpa: 3.73, ap: 4.36, nationalMean: 3.24, diff:  1.13 },
  { teacher: "T1", subject: "AP Chemistry",                 grade: 92.98, gpa: 3.77, ap: 4.21, nationalMean: 3.36, diff:  0.84 },
  { teacher: "T2", subject: "AP Chemistry",                 grade: 94.28, gpa: 3.84, ap: 4.13, nationalMean: 3.36, diff:  0.76 },
  { teacher: "T1", subject: "Calculus AB",                  grade: 90.39, gpa: 3.74, ap: 3.79, nationalMean: 3.21, diff:  0.59 },
  { teacher: "T2", subject: "Calculus AB",                  grade: 91.18, gpa: 3.77, ap: 3.41, nationalMean: 3.21, diff:  0.20 },
  { teacher: "T3", subject: "Calculus AB",                  grade: 93.03, gpa: 3.84, ap: 3.30, nationalMean: 3.21, diff:  0.09 },
  { teacher: "T1", subject: "Calculus BC",                  grade: 95.70, gpa: 3.88, ap: 4.80, nationalMean: 3.83, diff:  0.97 },
  { teacher: "T1", subject: "Comparative Government",       grade: 88.83, gpa: 3.61, ap: 3.17, nationalMean: 3.16, diff:  0.01 },
  { teacher: "T2", subject: "Comparative Government",       grade: 90.64, gpa: 3.64, ap: 2.93, nationalMean: 3.16, diff: -0.23 },
  { teacher: "T1", subject: "Computer Science A",           grade: 90.50, gpa: 3.82, ap: 4.10, nationalMean: 3.18, diff:  0.92 },
  { teacher: "T1", subject: "Computer Science Principles",  grade: 96.74, gpa: 3.71, ap: 3.75, nationalMean: 2.86, diff:  0.89 },
  { teacher: "T1", subject: "English Language",             grade: 92.05, gpa: 3.80, ap: 3.24, nationalMean: 3.19, diff:  0.05 },
  { teacher: "T1", subject: "English Literature",           grade: 90.26, gpa: 3.68, ap: 3.53, nationalMean: 3.23, diff:  0.29 },
  { teacher: "T1", subject: "European History",             grade: 96.63, gpa: 3.81, ap: 3.00, nationalMean: 3.27, diff: -0.27 },
  { teacher: "T1", subject: "Human Geography",              grade: 84.81, gpa: 3.46, ap: 3.25, nationalMean: 3.14, diff:  0.11 },
  { teacher: "T1", subject: "Macroeconomics",               grade: 93.85, gpa: 3.86, ap: 4.58, nationalMean: 3.20, diff:  1.38 },
  { teacher: "T1", subject: "Microeconomics",               grade: 93.85, gpa: 3.86, ap: 3.83, nationalMean: 3.24, diff:  0.59 },
  { teacher: "T1", subject: "Physics 1",                    grade: 96.00, gpa: 3.73, ap: 3.47, nationalMean: 3.13, diff:  0.35 },
  { teacher: "T1", subject: "Physics C: Mechanics",         grade: 92.08, gpa: 3.80, ap: 3.67, nationalMean: 3.30, diff:  0.37 },
  { teacher: "T2", subject: "Physics C: Mechanics",         grade: 96.00, gpa: 3.93, ap: 3.00, nationalMean: 3.30, diff: -0.30 },
  { teacher: "T1", subject: "Psychology",                   grade: 84.19, gpa: 3.55, ap: 4.08, nationalMean: 3.20, diff:  0.88 },
  { teacher: "T1", subject: "Seminar",                      grade: 93.43, gpa: 3.83, ap: 3.35, nationalMean: 3.17, diff:  0.18 },
  { teacher: "T1", subject: "United States History",        grade: 96.50, gpa: 3.83, ap: 4.50, nationalMean: 3.31, diff:  1.20 },
  { teacher: "T1", subject: "Art History",                  grade: 86.64, gpa: 3.58, ap: 3.07, nationalMean: 3.10, diff: -0.03 },
  { teacher: "T1", subject: "Statistics",                   grade: 93.10, gpa: 3.69, ap: 2.18, nationalMean: 2.92, diff: -0.74 },
  { teacher: "T1", subject: "2-D Art and Design",           grade: 90.00, gpa: 3.55, ap: 3.71, nationalMean: 3.33, diff:  0.39 },
  { teacher: "T1", subject: "Drawing",                      grade: 88.00, gpa: 3.51, ap: 4.00, nationalMean: 3.47, diff:  0.53 },
  { teacher: "T1", subject: "Spanish Language",             grade: 93.67, gpa: 3.81, ap: 4.00, nationalMean: 3.58, diff:  0.42 },
];

const DATA_BY_YEAR = {
  "2025": ALIGNMENT_DATA_2025,
};

// Only years we actually have data for appear as enabled in the year picker.
const AVAILABLE_YEARS = Object.keys(DATA_BY_YEAR).sort();
// Full picker range so users see the multi-year arc even before all years load.
const DISPLAY_YEARS = ["2020", "2021", "2022", "2023", "2024", "2025"];

// ─── Data hook ────────────────────────────────────────────────────────────────
// Kept as a hook so swapping to Firestore later is a drop-in change. When
// additional years arrive in an `apAlignment` collection, replace this body with:
//
//   useEffect(() => {
//     getDocs(query(collection(db, "apAlignment"), where("year", "==", year)))
//       .then(snap => setRows(snap.docs.map(d => d.data())));
//   }, [year]);
//
// Document shape already matches: { year, teacher, subject, grade, gpa, ap,
// nationalMean, diff }.
function useAlignmentData(year) {
  const rows = DATA_BY_YEAR[year] || [];
  return { data: rows, loading: false, error: null };
}

// ─── Constants ───────────────────────────────────────────────────────────────
const COLORS = {
  strong:       "#1D9E75",
  moderate:     "#BA7517",
  misalignment: "#E24B4A",
};

const FILTERS = [
  { key: "all",          label: "All sections"        },
  { key: "strong",       label: "Strong alignment"    },
  { key: "moderate",     label: "Moderate"            },
  { key: "misalignment", label: "Misalignment signal" },
];

const TABS = [
  { key: "alignment", label: "Alignment Index"  },
  { key: "trends",    label: "Trends Over Time" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function toPoint(r) {
  return {
    ...r,
    label: r.teacher ? `${r.subject} ${r.teacher}` : r.subject,
  };
}

function getCategory(diff) {
  if (diff >  0.5) return "strong";
  if (diff >= 0)   return "moderate";
  return "misalignment";
}
function getColor(diff) { return COLORS[getCategory(diff)]; }
function avg(arr, key)  { return arr.length ? arr.reduce((s, d) => s + d[key], 0) / arr.length : 0; }

// ─── Shared atoms ────────────────────────────────────────────────────────────
function EmptyState({ year }) {
  return (
    <div style={{ textAlign: "center", padding: "4rem", color: "#bbb", fontFamily: "monospace", fontSize: 12 }}>
      No alignment data available for {year}.
    </div>
  );
}

function YearPill({ value, active, disabled, onClick }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? "No data available yet" : undefined}
      style={{
        fontSize: 11, padding: "4px 11px", borderRadius: 4, border: "0.5px solid",
        borderColor: active ? "#1a1a18" : "#d4d3cc",
        background:  active ? "#1a1a18" : "transparent",
        color:       active ? "#fff"    : disabled ? "#ddd" : "#888",
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "monospace", transition: "all 0.15s",
        opacity: disabled ? 0.5 : 1,
      }}
    >{value}</button>
  );
}

// ─── Tab 1: Alignment Index ──────────────────────────────────────────────────
function AlignmentTab({ data, year }) {
  const [filter, setFilter]   = useState("all");
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState({ x: 0, y: 0, visible: false });
  const svgRef = useRef(null);

  if (!data.length) return <EmptyState year={year} />;

  const rows      = data.map(toPoint);
  const filtered  = filter === "all" ? rows : rows.filter(d => getCategory(d.diff) === filter);
  const statGrade = avg(filtered, "grade").toFixed(1);
  const statAP    = avg(filtered, "ap").toFixed(2);
  const statDiff  = avg(filtered, "diff");

  // Chart geometry
  const W = 640, H = 320;
  const PAD = { top: 20, right: 24, bottom: 48, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top  - PAD.bottom;
  const xMin = 82, xMax = 99, yMin = 1.8, yMax = 5.2;
  const xScale = v => ((v - xMin) / (xMax - xMin)) * chartW;
  const yScale = v => chartH - ((v - yMin) / (yMax - yMin)) * chartH;
  const xTicks = [84, 86, 88, 90, 92, 94, 96, 98];
  const yTicks = [2, 2.5, 3, 3.5, 4, 4.5, 5];

  function handleMouseMove(e, d) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
    setHovered(d);
  }
  function handleMouseLeave() {
    setTooltip(t => ({ ...t, visible: false }));
    setHovered(null);
  }

  return (
    <>
      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: "1.75rem" }}>
        {[
          { label: "avg course grade",  value: statGrade + "%" },
          { label: "avg AP score",      value: statAP },
          { label: "alignment index",   value: (statDiff >= 0 ? "+" : "") + statDiff.toFixed(2), color: getColor(statDiff) },
        ].map(s => (
          <div key={s.label} style={{ background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 400, color: s.color || "#1a1a18" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 20, marginBottom: "1rem", fontSize: 12, color: "#666", flexWrap: "wrap", alignItems: "center" }}>
        {[
          { label: "strong alignment (diff > 0.5)",  cat: "strong"       },
          { label: "moderate (0 – 0.5)",             cat: "moderate"     },
          { label: "misalignment signal (diff < 0)", cat: "misalignment" },
        ].map(l => (
          <span key={l.cat} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS[l.cat], display: "inline-block", flexShrink: 0 }} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Chart */}
      <div style={{ position: "relative", background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8, padding: "1rem", marginBottom: "1.25rem" }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }} onMouseLeave={handleMouseLeave}>
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {yTicks.map(t => <line key={t} x1={0} x2={chartW} y1={yScale(t)} y2={yScale(t)} stroke="#f5f4f0" strokeWidth={0.5} />)}
            {xTicks.map(t => <line key={t} x1={xScale(t)} x2={xScale(t)} y1={0} y2={chartH} stroke="#f5f4f0" strokeWidth={0.5} />)}
            <line x1={0} x2={chartW} y1={chartH} y2={chartH} stroke="#d4d3cc" strokeWidth={0.5} />
            <line x1={0} x2={0}      y1={0}      y2={chartH} stroke="#d4d3cc" strokeWidth={0.5} />
            {xTicks.map(t => <text key={t} x={xScale(t)} y={chartH + 16} textAnchor="middle" fontSize={10} fill="#999" fontFamily="monospace">{t}</text>)}
            {yTicks.map(t => <text key={t} x={-8} y={yScale(t) + 4} textAnchor="end"     fontSize={10} fill="#999" fontFamily="monospace">{t.toFixed(1)}</text>)}
            <text x={chartW / 2}  y={chartH + 38} textAnchor="middle" fontSize={11} fill="#888" fontFamily="monospace" letterSpacing="0.05em">course final average (%)</text>
            <text x={-chartH / 2} y={-36}         textAnchor="middle" fontSize={11} fill="#888" fontFamily="monospace" letterSpacing="0.05em" transform="rotate(-90)">AP score (1–5)</text>

            {rows.map(d => {
              const dim   = filter !== "all" && getCategory(d.diff) !== filter;
              const isHov = hovered?.label === d.label;
              return (
                <circle
                  key={d.label}
                  cx={xScale(d.grade)}
                  cy={yScale(d.ap)}
                  r={isHov ? 9 : 6}
                  fill={getColor(d.diff)}
                  opacity={dim ? 0.12 : isHov ? 1 : 0.82}
                  stroke={isHov ? "#1a1a18" : "transparent"}
                  strokeWidth={1.5}
                  style={{ cursor: "pointer", transition: "opacity 0.2s, r 0.15s" }}
                  onMouseMove={e => handleMouseMove(e, d)}
                />
              );
            })}
          </g>
        </svg>

        {tooltip.visible && hovered && (
          <div style={{
            position: "absolute", left: Math.min(tooltip.x + 14, W - 180), top: Math.max(tooltip.y - 10, 0),
            background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8, padding: "10px 14px",
            fontSize: 12, pointerEvents: "none", zIndex: 10, minWidth: 200,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)", fontFamily: "monospace",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13, fontFamily: "'Georgia',serif", color: "#1a1a18" }}>
              {hovered.label}
            </div>
            <div style={{ color: "#888", marginBottom: 2, fontSize: 11 }}>Course Avg: <strong style={{ color: "#1a1a18" }}>{hovered.grade.toFixed(1)}%</strong></div>
            <div style={{ color: "#888", marginBottom: 2, fontSize: 11 }}>AP Score: <strong style={{ color: "#1a1a18" }}>{hovered.ap.toFixed(2)}</strong></div>
            <div style={{ color: "#888", marginBottom: 2, fontSize: 11 }}>National: <strong style={{ color: "#1a1a18" }}>{hovered.nationalMean.toFixed(2)}</strong></div>
            <div style={{ color: "#888", fontSize: 11 }}>Alignment: <strong style={{ color: getColor(hovered.diff) }}>
              {hovered.diff >= 0 ? "+" : ""}{hovered.diff.toFixed(2)}
            </strong></div>
          </div>
        )}
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            fontSize: 11, padding: "5px 12px", borderRadius: 6, border: "0.5px solid",
            borderColor: filter === f.key ? "#1a1a18" : "#d4d3cc",
            background:  filter === f.key ? "#1a1a18" : "transparent",
            color:       filter === f.key ? "#fff"    : "#666",
            cursor: "pointer", fontFamily: "monospace", letterSpacing: "0.04em", transition: "all 0.15s",
          }}>{f.label}</button>
        ))}
      </div>

      {/* Detail table */}
      <div style={{ background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "10px 16px", borderBottom: "0.5px solid #e8e7e0", fontSize: 10, color: "#bbb", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace" }}>
          detail view · {filtered.length} records
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#FAFAF8" }}>
                {["Teacher / Course", "Course Avg", "AP Score", "National", "Alignment", "Category"].map(h => (
                  <th key={h} style={{
                    padding: "8px 14px", textAlign: "left", fontWeight: 400, color: "#bbb",
                    borderBottom: "0.5px solid #e8e7e0", fontFamily: "monospace", fontSize: 10,
                    letterSpacing: "0.06em", whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice().sort((a, b) => a.diff - b.diff).map((d, i) => (
                <tr key={d.label}
                  style={{ background: i % 2 === 0 ? "#fff" : "#FAFAF8", borderBottom: "0.5px solid #f0efe8", transition: "background 0.1s" }}
                  onMouseEnter={() => setHovered(d)} onMouseLeave={() => setHovered(null)}>
                  <td style={{ padding: "9px 14px", color: "#1a1a18" }}>{d.label}</td>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", color: "#444" }}>{d.grade.toFixed(1)}%</td>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", color: "#444" }}>{d.ap.toFixed(2)}</td>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", color: "#999" }}>{d.nationalMean.toFixed(2)}</td>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", color: getColor(d.diff), fontWeight: 600 }}>
                    {d.diff >= 0 ? "+" : ""}{d.diff.toFixed(2)}
                  </td>
                  <td style={{ padding: "9px 14px" }}>
                    <span style={{
                      fontSize: 10, padding: "3px 8px", borderRadius: 4,
                      background: getColor(d.diff) + "18", color: getColor(d.diff),
                      fontFamily: "monospace", letterSpacing: "0.05em", textTransform: "uppercase",
                    }}>{getCategory(d.diff)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: "1rem", fontSize: 10, color: "#ccc", fontFamily: "monospace", letterSpacing: "0.04em" }}>
        Alignment index = section AP score avg − national AP mean for that exam. Hover scatter points for detail.
      </div>
    </>
  );
}

// ─── Tab 2: Trends Over Time ─────────────────────────────────────────────────
function TrendsTab() {
  const years = AVAILABLE_YEARS;

  if (years.length < 2) {
    return (
      <div style={{
        background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8,
        padding: "3rem 2rem", textAlign: "center",
      }}>
        <div style={{ fontSize: 11, color: "#bbb", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace", marginBottom: 12 }}>
          trends unavailable
        </div>
        <div style={{ fontSize: 14, color: "#666", fontFamily: "'Georgia',serif", lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
          Only one year of alignment data is currently loaded ({years.join(", ")}).
          Trends will populate automatically as prior years are added to the dataset.
        </div>
      </div>
    );
  }

  // Build per-year, per-subject avg alignment
  const subjects = [...new Set(years.flatMap(y => (DATA_BY_YEAR[y] || []).map(r => r.subject)))].sort();
  const overall  = years.map(y => ({ year: y, value: avg(DATA_BY_YEAR[y] || [], "diff") }));

  const W = 700, H = 340, PAD = { top: 20, right: 20, bottom: 44, left: 50 };
  const cW = W - PAD.left - PAD.right, cH = H - PAD.top - PAD.bottom;
  const xPos = years.reduce((a, y, i) => { a[y] = (i / Math.max(1, years.length - 1)) * cW; return a; }, {});

  const allVals = overall.map(p => p.value).filter(Number.isFinite);
  const yMin = Math.min(-0.5, Math.floor(Math.min(...allVals) * 10) / 10);
  const yMax = Math.max( 1.0, Math.ceil (Math.max(...allVals) * 10) / 10);
  const yScale = v => cH - ((v - yMin) / (yMax - yMin)) * cH;
  const yTicks = [yMin, yMin + (yMax - yMin) * 0.25, yMin + (yMax - yMin) * 0.5, yMin + (yMax - yMin) * 0.75, yMax];

  return (
    <>
      <div style={{ background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8, padding: "1rem", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: 10, color: "#bbb", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace", marginBottom: 8 }}>
          school-wide alignment index over time
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {yTicks.map(t => <line key={t} x1={0} x2={cW} y1={yScale(t)} y2={yScale(t)} stroke="#f5f4f0" strokeWidth={0.5} />)}
            <line x1={0} x2={cW} y1={yScale(0)} y2={yScale(0)} stroke="#e8e7e0" strokeWidth={1} strokeDasharray="4,3" />
            <line x1={0} x2={0}  y1={0}  y2={cH} stroke="#d4d3cc" strokeWidth={0.5} />
            <line x1={0} x2={cW} y1={cH} y2={cH} stroke="#d4d3cc" strokeWidth={0.5} />
            {years.map(y => <text key={y} x={xPos[y]} y={cH + 15} textAnchor="middle" fontSize={9} fill="#ccc" fontFamily="monospace">{y}</text>)}
            {yTicks.map(t => <text key={t} x={-6} y={yScale(t) + 3} textAnchor="end" fontSize={9} fill="#ccc" fontFamily="monospace">{(t >= 0 ? "+" : "") + t.toFixed(2)}</text>)}
            <path
              d={overall.map((p, i) => `${i === 0 ? "M" : "L"} ${xPos[p.year]} ${yScale(p.value)}`).join(" ")}
              fill="none" stroke="#1a1a18" strokeWidth={2}
            />
            {overall.map(p => (
              <circle key={p.year} cx={xPos[p.year]} cy={yScale(p.value)} r={4} fill={getColor(p.value)} />
            ))}
          </g>
        </svg>
      </div>

      <div style={{ background: "#fff", border: "0.5px solid #d4d3cc", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "10px 16px", borderBottom: "0.5px solid #e8e7e0", fontSize: 10, color: "#bbb", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace" }}>
          avg alignment by subject · year over year
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#FAFAF8" }}>
                <th style={{ padding: "8px 14px", textAlign: "left", fontWeight: 400, color: "#bbb", borderBottom: "0.5px solid #e8e7e0", fontFamily: "monospace", fontSize: 10 }}>Subject</th>
                {years.map(y => (
                  <th key={y} style={{ padding: "8px 10px", textAlign: "center", fontWeight: 400, color: "#bbb", borderBottom: "0.5px solid #e8e7e0", fontFamily: "monospace", fontSize: 10 }}>{y}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subjects.map((subj, i) => (
                <tr key={subj} style={{ background: i % 2 === 0 ? "#fff" : "#FAFAF8", borderBottom: "0.5px solid #f0efe8" }}>
                  <td style={{ padding: "8px 14px", color: "#1a1a18" }}>{subj}</td>
                  {years.map(y => {
                    const rows = (DATA_BY_YEAR[y] || []).filter(r => r.subject === subj);
                    if (!rows.length) return <td key={y} style={{ padding: "8px 10px", textAlign: "center", color: "#ddd", fontFamily: "monospace" }}>—</td>;
                    const v = avg(rows, "diff");
                    return (
                      <td key={y} style={{ padding: "8px 10px", textAlign: "center", fontFamily: "monospace", color: getColor(v), fontWeight: 600 }}>
                        {(v >= 0 ? "+" : "") + v.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────────
export default function APAlignment() {
  const [tab, setTab]   = useState("alignment");
  const [year, setYear] = useState(AVAILABLE_YEARS[AVAILABLE_YEARS.length - 1] || "2025");
  const { data } = useAlignmentData(year);

  return (
    <div style={{
      fontFamily: "'Georgia','Times New Roman',serif",
      background: "#FAFAF8", minHeight: "100vh", padding: "2rem", color: "#1a1a18",
    }}>
      <div style={{ maxWidth: 940, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: "1.75rem", borderBottom: "1.5px solid #1a1a18", paddingBottom: "1rem" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#bbb", marginBottom: 6, fontFamily: "monospace" }}>
            King's Academy · AP Program Analysis · Madaba, Jordan
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 400, margin: 0, letterSpacing: "-0.02em" }}>AP Alignment Index</h1>
              <p style={{ fontSize: 13, color: "#888", margin: "4px 0 0", lineHeight: 1.5 }}>
                Comparing course final averages against AP exam scores and national means
              </p>
            </div>
            {tab === "alignment" && (
              <div>
                <div style={{ fontSize: 10, color: "#bbb", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace", marginBottom: 6 }}>exam year</div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {DISPLAY_YEARS.map(y => (
                    <YearPill
                      key={y}
                      value={y}
                      active={year === y}
                      disabled={!AVAILABLE_YEARS.includes(y)}
                      onClick={() => setYear(y)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", marginBottom: "1.75rem", borderBottom: "0.5px solid #d4d3cc" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              fontSize: 11, padding: "8px 20px", border: "none",
              borderBottom: tab === t.key ? "2px solid #1a1a18" : "2px solid transparent",
              background: "transparent", color: tab === t.key ? "#1a1a18" : "#bbb",
              cursor: "pointer", fontFamily: "monospace", letterSpacing: "0.06em",
              textTransform: "uppercase", transition: "all 0.15s", marginBottom: -1,
            }}>{t.label}</button>
          ))}
        </div>

        {tab === "alignment" && <AlignmentTab data={data} year={year} />}
        {tab === "trends"    && <TrendsTab />}

      </div>
    </div>
  );
}